import { HStack, Icon, InputBox, type InputBoxProps, VStack } from "@/shared/ui/primitives";
import { type ChangeEvent, useState } from "react";

export interface TextInputFieldProps extends InputBoxProps {
	label: string;
	subLabel?: string;
	subAction?: () => void;
	errorMessage?: string;
	clearable?: boolean;
}

export const shouldRenderSubAction = (subLabel?: string, subAction?: () => void) =>
	Boolean(subLabel && subAction);

export const getInputIconCount = (showClear: boolean, isPassword: boolean) =>
	(showClear ? 1 : 0) + (isPassword ? 1 : 0);

export const TextInputField = ({
	clearable,
	label,
	subLabel,
	subAction,
	errorMessage,
	...p
}: TextInputFieldProps) => {
	const [isVisible, setIsVisible] = useState(false);
	const isPassword = p.type === "password";

	const handleClear = () => {
		p.onChange?.({ target: { value: "" } } as ChangeEvent<HTMLInputElement>);
	};

	const hasValue = typeof p.value === "string" ? p.value.length > 0 : !!p.value;
	const showClear = Boolean(clearable && hasValue);
	const iconCount = getInputIconCount(showClear, isPassword);
	const showSubAction = shouldRenderSubAction(subLabel, subAction);

	return (
		<VStack className="w-full">
			<HStack className="w-full justify-between text-sm text-ct-strong font-medium p-[6px]">
				{label}
				{showSubAction ? (
					<button
						type="button"
						onClick={subAction}
						className="text-sm text-ct-regular font-normal"
						tabIndex={-1}
					>
						{subLabel}
					</button>
				) : null}
			</HStack>
			<div className="relative w-full">
				<InputBox
					className="w-full"
					{...p}
					type={isPassword && isVisible ? "text" : p.type}
					style={iconCount > 0 ? { paddingRight: iconCount > 1 ? 72 : 40, ...p.style } : p.style}
				/>
				{iconCount > 0 && (
					<HStack className="absolute right-[14px] top-1/2 -translate-y-1/2 gap-[4px]">
						{showClear && (
							<button type="button" onClick={handleClear} className="text-ct-regular" tabIndex={-1}>
								<Icon name="Delete" width={20} height={20} />
							</button>
						)}
						{isPassword && (
							<button
								type="button"
								onClick={() => setIsVisible(!isVisible)}
								className="text-ct-regular"
								tabIndex={-1}
							>
								<Icon name={isVisible ? "Invisible" : "Visible"} width={20} height={20} />
							</button>
						)}
					</HStack>
				)}
			</div>
			{errorMessage && (
				<span className="w-full px-[6px] py-[4px] text-sm text-dg-strong font-normal">
					{errorMessage}
				</span>
			)}
		</VStack>
	);
};
